<template>
  <div class="branch-select-comp relative">
    <!-- Selected Value -->
    <div class="flex items-center cursor-pointer py-1"
      @click="toggleDropdown()">
      <span class="text-[#171717] text-sm font-semibold truncate">{{
        selectedBranchName || placeholder
      }}</span>
      <i class="text-[#171717] text-xl ml-0.5 mt-0.5 font-semiboldflex-shrink-0"
        :class="isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
    </div>

    <!-- Dropdown List -->
    <div v-if="isOpen"
      class="absolute mt-2 w-[370px] -left-[60px] bg-[#fff] border border-[#D1D1D1] rounded shadow-md z-20 h-[390px] overflow-y-auto focus:shadow-[#4A7FFF] ">
      <!-- Search Input -->
      <div class="dropdown-input flex items-center px-2 py-1.5 border border-[#D1D1D1] bg-[#fff] m-3 rounded-lg">
        <i class="ri-search-line text-lg text-[#000] absolute right-6"></i>
        <input v-model="search" placeholder="Search Branch"
          class="dropdown-search flex-1 bg-transparent outline-none text-sm text-[#171717]" />
      </div>

      <!-- Closest Branch Section -->
      <div v-if="closestBranch && !search">
        <div class="px-3 py-2 text-sm text-[#6E6E6E]">
          Closest Branch
        </div>
        <div @click="selectBranch(closestBranch)"
          class="flex justify-between items-center px-3 py-2.5 text-sm transition-colors duration-200 hover:bg-[#F8F8F8] cursor-pointer"
          :class="[modelValue === closestBranch.BranchCode ? 'bg-[#F4EAEA]' : '']">
          <span class="truncate w-full text-[#464646]">{{ closestBranch.BranchName }}</span>
        </div>
      </div>

      <!-- Eligible Branches Section -->
      <div v-if="eligibleBranches.length > 0">
        <div class="px-3 py-2 text-sm text-[#6E6E6E]">
          {{ search ? 'Search Results' : 'Eligible Branches' }}
        </div>
        <div v-for="branch in eligibleBranches" :key="branch.BranchCode" @click="selectBranch(branch)"
          class="flex justify-between items-center px-3 py-2.5 text-sm transition-colors duration-200 hover:bg-[#F8F8F8] cursor-pointer"
          :class="[modelValue === branch.BranchCode ? 'bg-[#F4EAEA]' : '']">
          <span class="truncate w-full text-[#464646]">{{ branch.BranchName }}</span>
        </div>
      </div>

      <!-- No Results -->
      <div v-if="filteredBranches.length === 0" class="px-3 py-3 italic text-sm text-gray-500">
        No matches found
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  branches: {
    type: Array,
    required: true,
    // Expected format: [{ BranchCode: string, BranchName: string }]
  },
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select Branch'
  }
})

const emit = defineEmits(['update:modelValue', 'toggle', 'open', 'close'])

const isOpen = ref(false)
const search = ref('')

const selectedBranchName = computed(() => {
  const branch = props.branches.find(b => b.BranchCode === props.modelValue)
  return branch?.BranchName || ''
})

const filteredBranches = computed(() => {
  if (!search.value) return props.branches
  return props.branches.filter(b =>
    b.BranchName.toLowerCase().includes(search.value.toLowerCase())
  )
})

// Closest branch is the first one in the list
const closestBranch = computed(() => {
  if (!search.value && props.branches.length > 0) {
    return props.branches[0]
  }
  return null
})

// Eligible branches are all except the first (closest)
const eligibleBranches = computed(() => {
  if (search.value) {
    return filteredBranches.value
  }
  return props.branches.slice(1)
})

function selectBranch(branch) {
  emit('update:modelValue', branch.BranchCode)
  isOpen.value = false
  search.value = ''
  emit('close')
}

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    emit('toggle')
    emit('open')
  } else {
    emit('close')
  }
}

function clearSearch() {
  search.value = ''
}

function handleClickOutside(e) {
  if (!e.target.closest('.branch-select-comp')) {
    if (isOpen.value) {
      emit('close')
    }
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<style scoped>
.dropdown-search::placeholder {
  color: #9ca3af;
}
</style>
